import { Button, Grid } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  CPT_CODE_CATALOG_INITIAL_VALUES,
  cptCodeCatalogInitialValuesType,
} from "../../../../../constants/initial-schemas";
import Input from "../../../../Input";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useLoadingContext } from "../../../../../context/LoadingProvider";
import { useAddCataLogCode } from "../../../../../queries/catalog/use-add-catalog-code";
import { useUpdateCataLogCode } from "../../../../../queries/catalog/use-update-catalog-code";
import { useQueryClient } from "@tanstack/react-query";
import SideSheetDrawer from "../../../../SidesheetDrawer";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
} from "../../../../../utils/utils";

interface CptCodeCatalogDialogProps {
  dialogTitle: string;
  initialValues?: cptCodeCatalogInitialValuesType;
  disclosureHost: DisclosureHost;
  uuid?: string;
}

const cptCodeCatalogValidationSchema = Yup.object({
  cpt_code: Yup.string().trim().required().label("CPT Code"),
  description: Yup.string().trim().required().label("Description"),
});

const CptCodeCatalogDrawer = ({
  dialogTitle,
  initialValues,
  disclosureHost,
  uuid,
}: CptCodeCatalogDialogProps) => {
  const { setIsLoading } = useLoadingContext();
  const showSnackbar = useSnackbar();
  const { mutateAsync: addCptCode } = useAddCataLogCode();
  const { mutateAsync: editCptCode } = useUpdateCataLogCode();
  const queryClient = useQueryClient();

  const {
    handleBlur,
    handleChange,
    handleSubmit,
    values,
    resetForm,
    touched,
    errors,
  } = useFormik({
    initialValues: initialValues ?? CPT_CODE_CATALOG_INITIAL_VALUES,
    validationSchema: cptCodeCatalogValidationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  function resetAndClose() {
    resetForm();
    disclosureHost.close();
  }

  function onSubmit(values: cptCodeCatalogInitialValuesType) {
    setIsLoading(true);

    const transformedValues = {
      code: values.cpt_code.trim(),
      description: values.description.trim(),
    };

    (initialValues
      ? editCptCode({
          values: { ...transformedValues, uuid, type: "CPT" },
        })
      : addCptCode({ values: { ...transformedValues, type: "CPT" } })
    )
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));

        queryClient.invalidateQueries({
          queryKey: ["catalog-diagnosis-code-import"],
        });

        resetAndClose();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)))
      .finally(() => setIsLoading(false));
  }

  return (
    <SideSheetDrawer
      open={disclosureHost.opened}
      title={dialogTitle}
      onClose={resetAndClose}
      footerActions={
        <Button
          sx={{ width: "105px" }}
          onClick={() => handleSubmit()}
          variant="contained"
        >
          {dialogTitle?.includes("Edit") ? "Edit" : "Add"}
        </Button>
      }
    >
      <Grid container spacing={2} width="100%">
        <Grid item xs={12}>
          <Input
            required
            label="CPT Code"
            textFieldProps={{
              name: "cpt_code",
              value: values.cpt_code,
              onChange: handleChange,
              onBlur: handleBlur,
              placeholder: "Enter Code",
            }}
            error={touched.cpt_code && errors.cpt_code}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sx={{ "& .MuiInputBase-root": { padding: "8px 0 0 0 !important" } }}
        >
          <Input
            required
            label="Description"
            textFieldProps={{
              name: "description",
              value: values.description,
              onChange: handleChange,
              onBlur: handleBlur,
              placeholder: "Description",
              multiline: true,
              rows: 4,
            }}
            error={touched.description && errors.description}
          />
        </Grid>
      </Grid>
    </SideSheetDrawer>
  );
};
export default CptCodeCatalogDrawer;
